'use client'

import type { ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { ChevronDown } from 'lucide-react'
import { ViewSwitch, type ViewSwitchOption } from '@/components/ui/tls/view-switch'
import { categoryLabels, type BlogCategory } from '@/lib/blog-data'
import { MODEL_CHIPS, type ModelSlug } from '@/lib/blog-index'

type View = 'all' | BlogCategory

/** Four tabs, short enough to share one line at 390 with the More select. */
const VIEWS: readonly ViewSwitchOption<View>[] = [
  { id: 'all', label: 'All' },
  { id: 'tutorials', label: categoryLabels.tutorials },
  { id: 'tips-tricks', label: 'Tips' },
  { id: 'sound-spotlights', label: 'Spotlights' },
]

/** The categories that do not get a tab, chosen from More. */
const MORE: readonly BlogCategory[] = ['model-guides', 'news']

interface BlogPhoneFiltersProps {
  selectedCategory?: BlogCategory
  selectedModel?: ModelSlug
  /** A `?tag=` view: no category tab is selected. */
  noneSelected?: boolean
  className?: string
}

/**
 * The /blog filters below md. Line one is the category control: four tabs and
 * a More select for the rest, which takes the pressed underline while one of
 * its categories is shown. Line two is the model select. Both navigate to the
 * same URLs as the md+ link rows.
 */
export function BlogPhoneFilters({ selectedCategory, selectedModel, noneSelected = false, className = '' }: BlogPhoneFiltersProps) {
  const router = useRouter()
  const moreActive = selectedCategory !== undefined && MORE.includes(selectedCategory)
  const value: View | 'none' = noneSelected || moreActive ? 'none' : (selectedCategory ?? 'all')
  const goCategory = (id: string) => router.push(id === 'all' ? '/blog' : `/blog?category=${id}`)

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex flex-wrap items-center gap-x-4">
        <ViewSwitch<View | 'none'> label="Category" options={VIEWS} value={value} onChange={goCategory} />
        <SelectTab active={moreActive} label={moreActive && selectedCategory ? categoryLabels[selectedCategory] : 'More'}>
          <select
            aria-label="More categories"
            value={moreActive ? selectedCategory : ''}
            onChange={(e) => goCategory(e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            <option value="" disabled>More</option>
            {MORE.map((cat) => (
              <option key={cat} value={cat}>
                {categoryLabels[cat]}
              </option>
            ))}
          </select>
        </SelectTab>
      </div>
      <SelectTab
        active={Boolean(selectedModel)}
        label={
          <>
            <span className="font-medium text-[var(--tls-fg-subtle)]">Model:</span>
            {MODEL_CHIPS.find((c) => c.slug === selectedModel)?.label ?? 'Any'}
          </>
        }
        className="self-start"
      >
        <select
          aria-label="Model"
          value={selectedModel ?? ''}
          onChange={(e) => router.push(e.target.value ? `/blog?model=${e.target.value}` : '/blog')}
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          <option value="">Any model</option>
          {MODEL_CHIPS.map(({ slug, label }) => (
            <option key={slug} value={slug}>
              {label}
            </option>
          ))}
        </select>
      </SelectTab>
    </div>
  )
}

/**
 * A native select under a label the width of its text, with a chevron, the
 * same control as the library's "More" sorts. The label draws focus for it.
 */
function SelectTab({ active, label, children, className = '' }: { active: boolean; label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span
      className={`tls-target relative inline-flex cursor-pointer items-center gap-1 border-b-2 border-solid text-sm font-semibold has-[select:focus-visible]:outline has-[select:focus-visible]:outline-2 has-[select:focus-visible]:outline-offset-2 ${className}`}
      style={{
        borderBottomColor: active ? 'var(--tls-brand)' : 'transparent',
        color: active ? 'var(--tls-fg-strong)' : 'var(--tls-fg-muted)',
        outlineColor: 'var(--tls-brand)',
      }}
    >
      {label}
      <ChevronDown className="size-3.5" aria-hidden="true" />
      {children}
    </span>
  )
}
